import type { ParentRegistrationData } from '~pages/parents/bulk-upload/validation';
import type { ParentRegistrationColumnMappingTableData } from '~pages/parents/bulk-upload/types';

/**
 * ENR-3822: old Bulk Upload keeps progress only in React state. Import History
 * is a sibling route, so the wizard unmounts. Persist a session draft so
 * returning restores step, CSV rows, headers, and column mapping.
 */
export const PARENTS_BULK_UPLOAD_DRAFT_KEY =
  'enrollio.parents-bulk-upload.draft';

export type BulkUploadStep = 'select' | 'mapping' | 'validate';

export type ParentsBulkUploadDraft = {
  fileName: string;
  step: BulkUploadStep;
  csvData: Array<ParentRegistrationData>;
  headers: Array<string>;
  columnMappingTableData: Array<ParentRegistrationColumnMappingTableData>;
};

const STEPS: ReadonlyArray<BulkUploadStep> = ['select', 'mapping', 'validate'];

function isDraft(value: unknown): value is ParentsBulkUploadDraft {
  if (!value || typeof value !== 'object') return false;
  const draft = value as ParentsBulkUploadDraft;
  return (
    typeof draft.fileName === 'string' &&
    draft.fileName.length > 0 &&
    STEPS.includes(draft.step) &&
    Array.isArray(draft.csvData) &&
    Array.isArray(draft.headers) &&
    Array.isArray(draft.columnMappingTableData)
  );
}

export function readParentsBulkUploadDraft(
  storage: Pick<Storage, 'getItem'> = sessionStorage,
): ParentsBulkUploadDraft | null {
  try {
    const raw = storage.getItem(PARENTS_BULK_UPLOAD_DRAFT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isDraft(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeParentsBulkUploadDraft(
  draft: ParentsBulkUploadDraft,
  storage: Pick<Storage, 'setItem'> = sessionStorage,
): void {
  try {
    storage.setItem(PARENTS_BULK_UPLOAD_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Quota or private mode — wizard still works for this visit.
  }
}

export function clearParentsBulkUploadDraft(
  storage: Pick<Storage, 'removeItem'> = sessionStorage,
): void {
  try {
    storage.removeItem(PARENTS_BULK_UPLOAD_DRAFT_KEY);
  } catch {
    // ignore
  }
}

export function fileFromDraft(draft: ParentsBulkUploadDraft, csvText: string) {
  return new File([csvText], draft.fileName || 'parents.csv', {
    type: 'text/csv',
  });
}
